import React, { useState, useRef, useEffect } from "react";
import { Upload, Tooltip, notification, message } from "antd";
import { useTranslation } from "react-i18next";
import { history } from "../../../../../redux";
import { removeContentExtensions } from "../../../../../services/contents";
import { hasAccessRole } from "../../../../../services/utils";
import UserRoleEnum from "../../../../../constants/UserRoleEnum";
import VerifiedAgencyInfoModal from "../../../../shared/components/VerifiedAgencyInfoModal";
import ChangeAdContainer from "../../containers/ChangeAdContainer";

const apiUrl = process.env.REACT_APP_API_ENDPOINT;

const ChangeAdButton = (props) => {
  const { t } = useTranslation();
  const {
    user,
    token,
    campaign,
    relation,
    loadingChangeSpot,
    loadingChangeSpotSuccess,
    loadingChangeSpotInCampign,
    newSpotsChanged,
    updateContentCampaignRequest,
    updateContentDisplayByContent,
    loadingContentInCampaign,
  } = props;

  const [agencyModalVisible, setAgencyModalVisible] = useState(false);
  const [libraryVisible, setLibraryVisible] = useState(false);

  const loadingChangeSpotRef = useRef(loadingChangeSpot);
  const loadingChangeSpotSuccessRef = useRef(loadingChangeSpotSuccess);
  const newSpotsChangedRef = useRef(newSpotsChanged);

  useEffect(() => {
    loadingChangeSpotRef.current = loadingChangeSpot;
  }, [loadingChangeSpot]);
  useEffect(() => {
    loadingChangeSpotSuccessRef.current = loadingChangeSpotSuccess;
  }, [loadingChangeSpotSuccess]);
  useEffect(() => {
    newSpotsChangedRef.current = newSpotsChanged;
  }, [newSpotsChanged]);

  const uploadTimeExceededSpot = (placement, text) => {
    notification.error({
      message: text + "(" + relation.display_slot_length / 1000 + " seg)",
      description: t(
        "This would modify the value of the campaign, create a new campaign or contact support"
      ),
      duration: 10,
      placement,
    });
  };

  const whileRequestUpdateContent = async (content) => {
    const newContent = {
      content_id: content.id,
      content_name: content.name,
      content_type: content.type,
      file_thumb: content.file_thumb,
      file: content.file,
      content_file: content.file,
      content_file_thumb: content.file_thumb,
      content_width: content.width,
      content_height: content.height,
    };
    if (content.type === "image") {
      updateContentCampaignRequest(content, relation, campaign.id);
      updateContentDisplayByContent(newContent, relation);
    } else {
      let stop = false;
      updateContentCampaignRequest(content, relation, campaign.id);
      while (
        (!content.ready && !stop) ||
        (newSpotsChangedRef.current[0] &&
          newSpotsChangedRef.current[0].content.file_thumb === "pending")
      ) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const campaignRoute = "/campaign/" + campaign.id;
        if (
          history.location.pathname === campaignRoute &&
          loadingChangeSpotRef.current
        ) {
          updateContentCampaignRequest(content, relation, campaign.id);
        } else if (!loadingChangeSpotSuccessRef.current) {
          stop = true;
        }
      }
      if (!newSpotsChangedRef.current[0]) return;
      newContent.file = newSpotsChangedRef.current[0].content.file;
      newContent.file_thumb = newSpotsChangedRef.current[0].content.file_thumb;
      newContent.content_file = newSpotsChangedRef.current[0].content.file;
      newContent.content_file_thumb =
        newSpotsChangedRef.current[0].content.file_thumb;
      updateContentDisplayByContent(newContent, relation);
    }
  };

  const uploadProps = {
    name: "file",
    accept: "video/*,image/*,video/mp4,video/x-m4v",
    action: `${apiUrl}/contents`,
    data: (file) => ({ name: removeContentExtensions(file.name) }),
    multiple: false,
    beforeUpload(file) {
      const isLt2M = file.size / 1024 / 1024 < 50;
      if (!isLt2M) message.error(t("The file cannot be larger than 50MB!"), 10);
      return isLt2M;
    },
    headers: { authorization: `Bearer ${token}` },
    onChange: async (info) => {
      loadingContentInCampaign(true);
      const status = info.file.status;
      if (status === "done") {
        if (
          (user && hasAccessRole(user.roles, [UserRoleEnum.companyMember])) ||
          info.file.response.length < relation.display_slot_length + 1000
        ) {
          message.success(`${info.file.name} ${t("successfully uploaded")}`);
          whileRequestUpdateContent(info.file.response);
        } else if (relation.belong_to_smart_campaign) {
          uploadTimeExceededSpot(
            "topRight",
            t(
              "In programmatic the ads should not exceed the maximum time of the advertising space on the screen, otherwise they could be cut off in this time"
            )
          );
        } else {
          uploadTimeExceededSpot(
            "topRight",
            t("You cannot upload files longer than what is allowed on the screen")
          );
        }
        loadingContentInCampaign(false);
      } else if (status === "error") {
        message.error(`${t("Upload error")} ${info.file.name}`);
        loadingContentInCampaign(false);
      }
    },
  };

  const cmsTooltip =
    relation.display_use_cms === 1
      ? t(
          "Change this ad for another, requires approval. Remember to upload ads of the same client, brand, campaign and similar duration."
        )
      : t(
          "Change this ad for another, requires approval. Remember to upload ads of the same client, brand, campaign and similar duration. No immediately"
        );

  const isUnverifiedAgency =
    user &&
    user.company &&
    user.company.type === "agency" &&
    !user.company.is_verified;

  if (isUnverifiedAgency) {
    return (
      <>
        <button
          type="button"
          className="screenAd__change"
          onClick={() => setAgencyModalVisible(true)}
        >
          {t("Change ad")}
        </button>
        <VerifiedAgencyInfoModal
          isVisible={agencyModalVisible}
          hide={() => setAgencyModalVisible(false)}
        />
      </>
    );
  }

  if (campaign.new_ssp) {
    return (
      <>
        <Tooltip placement="top" title={cmsTooltip}>
          <button
            type="button"
            className="screenAd__change"
            disabled={loadingChangeSpotSuccess || loadingChangeSpotInCampign}
            onClick={() => setLibraryVisible(true)}
          >
            {t("Change ad")}
          </button>
        </Tooltip>
        {libraryVisible && (
          <ChangeAdContainer
            visible={libraryVisible}
            relation={relation}
            exitModal={() => setLibraryVisible(false)}
          />
        )}
      </>
    );
  }

  return (
    <Upload
      {...uploadProps}
      disabled={loadingChangeSpotSuccess || loadingChangeSpotInCampign}
    >
      <Tooltip placement="top" title={cmsTooltip}>
        <button
          type="button"
          className="screenAd__change"
          disabled={loadingChangeSpotSuccess || loadingChangeSpotInCampign}
        >
          {t("Change ad")}
        </button>
      </Tooltip>
    </Upload>
  );
};

export default ChangeAdButton;
